/* eslint-disable react/prop-types */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Col, Row, Button, ButtonGroup } from 'react-bootstrap';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Font
} from '@react-pdf/renderer';

// Register custom hyphenation callback to prevent word breaks
Font.registerHyphenationCallback(word => {
  // Return the word as a single unit (no hyphenation)
  return [word];
});

const TableComponent = ({ data }) => {
  const [tableData, setTableData] = useState([]);
  const [title, setTitle] = useState('');
  const tableRef = useRef(null);

  // Process and set data when it changes
  useEffect(() => {
    // Safely extract data from the API response
    if (data?.datas && Array.isArray(data.datas) && data.datas.length > 0) {
      // Make a deep copy to avoid reference issues
      const processedData = JSON.parse(JSON.stringify(data.datas));
      setTableData(processedData);
    } else {
      setTableData([]);
    }

    // Set the title if available
    if (data?.title?.value) {
      setTitle(data.title.value);
    }
  }, [data]);

  // Generate columns using memoization to prevent unnecessary rerenders
  const columns = useMemo(() => {
    if (!tableData.length) return [];

    return Object.keys(tableData[0])
      .filter(key => key !== 'is_grand_total') // Hide the is_grand_total column from display
      .map(key => ({
        accessor: key,
        Header: key,
        // Cell renderer that formats content and handles HTML
        Cell: ({ value }) => {
          if (value === null || value === undefined) {
            return '-';
          }

          // For HTML content
          if (typeof value === 'string' && value.includes('<a href=')) {
            return <div dangerouslySetInnerHTML={{ __html: value }} />;
          }

          // Return the value as is for everything else
          return value;
        }
      }));
  }, [tableData]);

  // Export to Excel function with heading
  const exportToExcel = () => {
    // Skip if no data
    if (!tableData.length) return;

    // Create a copy of data without the is_grand_total column for export
    const exportData = [];

    // Create an array for the title row
    const titleRowData = [];
    titleRowData.push(title || 'Report Data'); // First cell contains the title

    // Get all columns except is_grand_total
    const columnKeys = Object.keys(tableData[0]).filter(
      key => key !== 'is_grand_total'
    );

    // Add empty cells for the remaining columns
    for (let i = 1; i < columnKeys.length; i++) {
      titleRowData.push('');
    }

    // Create a blank row for spacing
    const blankRow = Array(columnKeys.length).fill('');

    // Process table data for export
    const tableDataForExport = tableData.map(row => {
      const newRow = { ...row };
      delete newRow.is_grand_total; // Remove the is_grand_total field
      return Object.values(newRow); // Convert to array of values
    });

    // Create worksheet with all rows
    const ws = XLSX.utils.aoa_to_sheet([
      titleRowData, // Title row
      blankRow, // Blank row for spacing
      columnKeys, // Headers
      ...tableDataForExport // Data rows
    ]);

    // Set title cell style
    const titleCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
    ws[titleCell].s = {
      font: {
        bold: true,
        sz: 16
      }
    };

    // Merge title cells
    if (!ws['!merges']) {
      ws['!merges'] = [];
    }
    ws['!merges'].push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: columnKeys.length - 1 }
    });

    // Style for headers
    const headerStyle = {
      font: {
        bold: true
      },
      fill: {
        fgColor: { rgb: 'C7DBFF' } // Light blue
      }
    };

    // Apply header styles
    for (let i = 0; i < columnKeys.length; i++) {
      const headerCell = XLSX.utils.encode_cell({ r: 2, c: i });
      if (ws[headerCell]) {
        ws[headerCell].s = headerStyle;
      }
    }

    // Find and style Grand Total rows
    for (let r = 3; r < 3 + tableDataForExport.length; r++) {
      const firstCell = XLSX.utils.encode_cell({ r, c: 0 });
      const cellValue = ws[firstCell] ? ws[firstCell].v : '';

      if (cellValue === 'Grand Total') {
        // Apply Grand Total styling to all cells in this row
        for (let c = 0; c < columnKeys.length; c++) {
          const cell = XLSX.utils.encode_cell({ r, c });
          if (ws[cell]) {
            ws[cell].s = {
              fill: {
                fgColor: { rgb: '0D6EFD' }, // Bootstrap primary color
                type: 'pattern',
                patternType: 'solid'
              },
              font: {
                color: { rgb: 'FFFFFF' }, // White text
                bold: true
              }
            };
          }
        }
      }
    }

    // Set column widths
    const wscols = [];
    for (let i = 0; i < columnKeys.length; i++) {
      wscols.push({ wch: Math.max(15, columnKeys[i].length + 2) });
    }
    ws['!cols'] = wscols;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, ws, 'Report Data');

    // Generate a filename with current date
    const fileName = `${title || 'Report'}_${
      new Date().toISOString().split('T')[0]
    }.xlsx`;

    // Export workbook
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
      bookSST: false,
      cellStyles: true
    });
    const fileData = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    // Save file
    saveAs(fileData, fileName);
  };

  // Define styles for react-pdf
  const styles = StyleSheet.create({
    page: {
      padding: 15, // Further reduced padding to increase usable width
      backgroundColor: '#FFFFFF',
      width: '100%' // Ensure full width is used
    },
    title: {
      fontSize: 16,
      marginBottom: 12,
      textAlign: 'center'
    },
    table: {
      display: 'flex',
      width: '100%',
      borderStyle: 'solid',
      borderWidth: 1,
      borderColor: '#CCCCCC',
      marginBottom: 10,
      borderCollapse: 'collapse' // Added to help connect borders
    },
    tableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#CCCCCC',
      borderBottomStyle: 'solid',
      minHeight: 25, // Reduced from 30
      alignItems: 'center',
      width: '100%' // Ensure full width
    },
    tableRowHeader: {
      backgroundColor: '#C7DBFF'
    },
    tableRowGrandTotal: {
      backgroundColor: '#0D6EFD',
      fontWeight: 'bold',
      color: '#FFFFFF'
    },
    tableCell: {
      flex: 1,
      padding: 4, // Reduced from 5
      fontSize: 7, // Reduced from 8
      borderRightWidth: 1, // Added vertical grid line
      borderRightColor: '#CCCCCC', // Added vertical grid line
      borderRightStyle: 'solid', // Added vertical grid line
      height: '100%', // Make cells fill the entire row height
      display: 'flex',
      justifyContent: 'center'
    },
    tableCellLast: {
      borderRightWidth: 0 // No right border for the last cell
    },
    tableCellHeader: {
      fontWeight: 'bold',
      fontSize: 7 // Reduced from 8
    },
    tableCellText: {
      width: '100%',
      textAlign: 'left',
      // The hyphenation is handled by the global Font.registerHyphenationCallback
      // These styles ensure proper text display
      wordBreak: 'normal',
      whiteSpace: 'normal'
    }
  });

  // Create PDF Document component
  const MyDocument = () => (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page} wrap={false}>
        <Text style={styles.title}>{title || 'Report Data'}</Text>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.tableRowHeader]}>
            {columns.map((column, i) => (
              <View
                key={`header-${i}`}
                style={[
                  styles.tableCell,
                  i === columns.length - 1 ? styles.tableCellLast : null
                ]}
              >
                <Text style={[styles.tableCellHeader, styles.tableCellText]}>
                  {column.Header}
                </Text>
              </View>
            ))}
          </View>

          {/* Table Rows */}
          {tableData.map((row, rowIndex) => {
            const isGrandTotal = row['District Name'] === 'Grand Total';
            return (
              <View
                key={`row-${rowIndex}`}
                style={[
                  styles.tableRow,
                  isGrandTotal ? styles.tableRowGrandTotal : null
                ]}
              >
                {columns.map((column, colIndex) => {
                  // Get cell value
                  const cellValue =
                    row[column.accessor] === null ||
                    row[column.accessor] === undefined
                      ? '-'
                      : String(row[column.accessor]);

                  return (
                    <View
                      key={`cell-${rowIndex}-${colIndex}`}
                      style={[
                        styles.tableCell,
                        colIndex === columns.length - 1
                          ? styles.tableCellLast
                          : null
                      ]}
                    >
                      <Text
                        style={[
                          styles.tableCellText,
                          isGrandTotal ? { color: 'white' } : null
                        ]}
                      >
                        {cellValue}
                      </Text>
                    </View>
                  );
                })}
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );

  // Function to handle PDF Export
  const exportToPDF = async () => {
    // Skip if no data
    if (!tableData.length) return;

    try {
      // Generate a filename with current date
      const fileName = `${title || 'Report'}_${
        new Date().toISOString().split('T')[0]
      }.pdf`;

      // Create PDF blob
      const blob = await pdf(<MyDocument />).toBlob();
      saveAs(blob, fileName);
    } catch (error) {
      console.error('PDF Export Error:', error);
    }
  };

  // Function to check if a row is a grand total row by checking District Name
  const isGrandTotalRow = row => {
    return row?.original['District Name'] === 'Grand Total';
  };

  // Row style function to add custom inline styles for better control
  const getRowStyle = row => {
    if (isGrandTotalRow(row)) {
      return {
        background: 'var(--bs-primary)',
        color: 'white',
        fontWeight: 'bold'
      };
    }
    return {};
  };

  return (
    <Row className="gx-3">
      <Col>
        <AdvanceTableWrapper
          columns={columns}
          data={tableData}
          sortable
          pagination
          perPage={50}
        >
          <Card>
            <FalconCardHeader
              title={title || 'Report Data'}
              light
              endEl={
                <ButtonGroup size="sm">
                  <Button
                    variant="falcon-default"
                    onClick={exportToExcel}
                    className="me-2"
                  >
                    <i className="fas fa-file-excel me-1"></i> Export to Excel
                  </Button>
                  <Button variant="falcon-default" onClick={exportToPDF}>
                    <i className="fas fa-file-pdf me-1"></i> Export to PDF
                  </Button>
                </ButtonGroup>
              }
            />
            <Card.Body className="p-0">
              <div ref={tableRef}>
                <AdvanceTable
                  table
                  headerClassName="bg-200 text-900 text-nowrap align-middle"
                  rowClassName={row => {
                    // Check if this is a grand total row using District Name
                    const isGrandTotal = isGrandTotalRow(row);
                    return `align-middle white-space-nowrap ${
                      isGrandTotal ? 'bg-light text-white fw-bold' : ''
                    }`;
                  }}
                  tableProps={{
                    striped: false,
                    className: 'fs--1 mb-0 overflow-hidden'
                  }}
                />
              </div>
            </Card.Body>
            <Card.Footer>
              <AdvanceTableFooter
                rowCount={tableData.length}
                table
                rowInfo
                navButtons
                rowsPerPageSelection
              />
            </Card.Footer>
          </Card>
        </AdvanceTableWrapper>
      </Col>
    </Row>
  );
};

export default TableComponent;
